<template>
  <div class="p-4 md:p-8 max-w-3xl mx-auto">

    <!-- Profile Card -->
    <el-card class="!rounded-xl !border-gray-200 shadow-sm overflow-visible" shadow="hover">
      <template #header v-if="!inDialog">
        <div class="flex items-center justify-between">
          <span class="text-lg font-bold text-gray-900">{{ $t('Account Status') }}</span>
          <el-tag :type="isSubscribed ? 'success' : 'info'" effect="dark" round size="large">
            {{ isSubscribed ? $t('Premium') : $t('Free') }}
          </el-tag>
        </div>
      </template>

      <!-- Loading State -->
      <div v-if="loading" class="py-12 flex flex-col items-center justify-center min-h-[200px]">
         <el-icon class="is-loading text-blue-500 mb-3" :size="32"><Loading /></el-icon>
         <span class="text-gray-500 text-sm font-medium">{{ loadingMessage }}</span>
      </div>

      <!-- Main Content -->
      <div v-else class="flex flex-col md:flex-row gap-8 items-start py-4">

        <!-- Visual Status -->
        <div class="flex-shrink-0 mx-auto md:mx-0">
          <div class="w-32 h-32 rounded-full bg-gray-50 flex items-center justify-center border-4 border-white shadow-lg relative bg-gradient-to-b from-gray-50 to-gray-100">
             <div v-if="isSubscribed" class="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500/10 to-indigo-600/10"></div>
             <img :src="isSubscribed ? VIP : NOVIP" class="w-20 h-20 object-contain z-10 drop-shadow-sm" alt="Status Icon" loading="lazy"/>
          </div>
        </div>

        <!-- Info Area -->
        <div class="flex-1 w-full">

           <!-- Subscribed View -->
           <div v-if="isSubscribed" class="animate-fade-in">
              <h2 class="text-2xl font-bold text-gray-800 mb-2">{{ $t('VIPMember') }}</h2>

              <div class="bg-blue-50 rounded-lg p-4 border border-blue-100 mb-6">
                <div class="flex items-center gap-2 mb-1">
                  <el-icon class="text-blue-600"><Calendar /></el-icon>
                  <span class="text-sm font-medium text-blue-900">{{ $t('ExpiresOn') }}</span>
                </div>
                <div class="text-lg font-mono text-blue-700 font-bold ml-6">
                  {{ formattedExpiration }}
                </div>
              </div>
           </div>

           <!-- Free View -->
           <div v-else class="animate-fade-in">
              <h2 class="text-xl font-bold text-gray-800 mb-2">{{ $t('NOVIP') }}</h2>
              <p class="text-gray-500 mb-6 leading-relaxed">{{ $t('UpgradeHint') }}</p>

              <!-- Restore Purchase Section -->
              <div class="bg-gray-50 p-5 rounded-xl border border-gray-200">
                <div class="flex items-center gap-2 mb-3">
                  <el-icon class="text-gray-400"><Refresh /></el-icon>
                  <span class="text-xs font-bold text-gray-500 uppercase tracking-wider">{{ $t('Restore') }}</span>
                </div>

                <el-form :model="formData" ref="emailForm" :rules="rules" @submit.prevent class="restore-form">
                  <div class="flex flex-col sm:flex-row gap-3">
                    <el-form-item prop="email" class="!mb-0 flex-1">
                      <el-input
                        v-model="formData.email"
                        :placeholder="$t('EmailRequired')"
                        type="email"
                      />
                    </el-form-item>
                    <el-form-item class="!mb-0">
                      <el-button type="primary" :loading="loading" @click="validateAndGetData">
                        {{ $t('Restore') }}
                      </el-button>
                    </el-form-item>
                  </div>
                </el-form>
              </div>
           </div>

        </div>
      </div>
    </el-card>

    <div class="text-center mt-8 space-y-2">
       <p class="text-xs text-gray-400 font-mono">ID: {{ displayEmail }}</p>
       <p class="text-xs text-gray-300">Copyright © emperinter</p>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { ElNotification } from 'element-plus';
import { Loading, Calendar, Refresh } from '@element-plus/icons-vue';
import { config } from '@/config';
import VIP from '@/assets/vip.png';
import NOVIP from '@/assets/novip.png';

export default {
  name: 'AccountPage',
  components: {
    Loading, Calendar, Refresh
  },
  props: {
    inDialog: Boolean
  },
  data() {
    return {
      VIP, NOVIP,
      loading: false,
      loadingType: '',
      userInfo: null,
      transactionInfo: null,
      isSubscribed: false,
      productName: '',
      expirationTime: null,
      formData: { email: '' },
      rules: {
        email: [
          { required: true, message: this.$t('EmailRequired'), trigger: 'blur' },
          { type: 'email', message: this.$t('ValidEmail'), trigger: ['blur', 'change'] }
        ]
      }
    };
  },
  computed: {
    loadingMessage() {
      return this.loadingType === 'validating' ? this.$t('Validating') :
             this.loadingType === 'fetching' ? this.$t('Subscribing') :
             this.$t('Loading');
    },
    formattedExpiration() {
      if (!this.expirationTime) return '';
      return new Date(this.expirationTime).toLocaleDateString() + ' ' + new Date(this.expirationTime).toLocaleTimeString();
    },
    displayEmail() {
      return this.formData.email || localStorage.getItem("email") || 'Guest';
    }
  },
  mounted() {
    this.refreshLocalState();
  },
  methods: {
    refreshLocalState() {
      this.expirationTime = localStorage.getItem("expirationTime");
      const storedEmail = localStorage.getItem("email");
      if (storedEmail) this.formData.email = storedEmail;

      if (this.expirationTime && new Date() < new Date(this.expirationTime)) {
        this.isSubscribed = true;
      } else {
        this.isSubscribed = false;
        if (localStorage.getItem("purchaseStatus") === 'purchased') {
          localStorage.setItem("purchaseStatus", "expired");
        }
      }
    },

    async validateAndGetData() {
      this.loading = true;
      this.loadingType = 'validating';

      try {
        await this.$refs.emailForm.validate();
        const email = this.formData.email;

        // Developer Backdoor
        if (email === 'emperinter#@outlook.com') {
          this.grantDevAccess(email);
          return;
        }

        this.loadingType = 'fetching';
        await this.fetchSubscriptionData(email);

      } catch (error) {
        if (error?.message) {
          console.warn("Validation or Fetch Error", error);
        }
        ElNotification({
          title: this.$t("Warning"),
          message: this.$t("InvalidUser"),
          type: 'warning'
        });
      } finally {
        this.loading = false;
        this.loadingType = '';
      }
    },

    grantDevAccess(email) {
      const expirationTime = new Date();
      expirationTime.setDate(expirationTime.getDate() + 365);

      localStorage.setItem('purchaseStatus', 'purchased');
      localStorage.setItem('expirationTime', expirationTime.toISOString());
      localStorage.setItem('email', email);

      this.refreshLocalState();
      ElNotification({ title: 'Success', message: 'Developer Access Granted', type: 'success' });
    },

    async fetchSubscriptionData(email) {
      try {
        const response = await axios.get(`${config.apiUrl}/v1/subscriptions`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${config.apiKey}`
          },
          params: {
            filter: {
              user_email: email
            }
          }
        });

        const subscriptionsData = response.data.data;
        if (subscriptionsData.length > 0) {
          const activeSub = subscriptionsData.find(sub =>
            sub.attributes.status === 'active' &&
            new Date(sub.attributes.renews_at) > new Date()
          );

          if (activeSub) {
            this.isSubscribed = true;
            this.expirationTime = activeSub.attributes.renews_at;
            localStorage.setItem('email', email);
            localStorage.setItem('purchaseStatus', 'purchased');
            localStorage.setItem('expirationTime', this.expirationTime);
            ElNotification({ title: this.$t('Success'), message: this.$t('Purchase Restored'), type: 'success' });
          } else {
            this.isSubscribed = false;
            ElNotification({
              title: this.$t("Info"),
              message: this.$t("NoActiveSubscription"),
              type: 'info'
            });
          }
        } else {
          this.isSubscribed = false;
          ElNotification({
            title: this.$t("Info"),
            message: this.$t("NoSubscriptions"),
            type: 'info'
          });
        }
      } catch (error) {
        console.error('Error occurred:', error);
        ElNotification({
          title: this.$t("Error"),
          message: this.$t("FetchError"),
          type: 'error'
        });
      }
    }
  }
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
